import axios from 'axios';

// Demo credentials for testing
const DEMO_MERCHANT_ID = 'DEMO001';
const DEMO_API_KEY = 'demo_api_key_12345';

// Create axios instance with base configuration
// Using relative URL to leverage webpack proxy (like FeeTierManager)
const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

// Function to generate SHA256 signature
const generateSignature = async (merchantID, timestamp, requestBody, apiKey) => {
  const stringToHash = merchantID + timestamp + requestBody + apiKey;

  // Use Web Crypto API for SHA256 hashing
  const encoder = new TextEncoder();
  const data = encoder.encode(stringToHash);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

// Request interceptor to add signature authentication
api.interceptors.request.use(
  async (config) => {
    // Skip authentication for product routing endpoints
    if (config.url && config.url.includes('/product-routing/')) {
      config.headers = {
        ...config.headers,
        'Content-Type': 'application/json',
      };
      return config;
    }

    // Skip signature auth for admin/supplier endpoints in development
    if (config.url && (
      config.url.includes('/suppliers/') ||
      config.url.includes('/categories/') ||
      config.url.includes('/subcategories/') ||
      config.url.includes('/master-products/') ||
      config.url.includes('/admin/')
    )) {
      config.headers = {
        ...config.headers,
        'Content-Type': 'application/json',
      };
      return config;
    }

    // Check if crypto.subtle is available (secure context: HTTPS or localhost)
    if (!crypto.subtle) {
      console.warn('Web Crypto API not available - skipping signature auth');
      config.headers = {
        ...config.headers,
        'Content-Type': 'application/json',
      };
      return config;
    }

    // Get merchant credentials (in production, get from environment or config)
    const merchantID = process.env.REACT_APP_MERCHANT_ID || DEMO_MERCHANT_ID;
    const apiKey = process.env.REACT_APP_API_KEY || DEMO_API_KEY;

    // Generate timestamp
    const timestamp = Math.floor(Date.now() / 1000);

    // Get request body for signature generation
    const requestBody = config.data ? JSON.stringify(config.data) : '';

    // Generate signature
    const signature = await generateSignature(merchantID, timestamp, requestBody, apiKey);

    // Add required headers
    config.headers = {
      ...config.headers,
      'Content-Type': 'application/json',
      'X-Merchant-Code': merchantID,
      'X-API-Key': apiKey,
      'X-Signature': signature,
      'X-Timestamp': timestamp.toString()
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication failed - Invalid signature');
      // In signature auth, 401 means invalid signature
      // You might want to show an error message instead of redirecting
    }
    return Promise.reject(error);
  }
);

export default api;